import React, { useEffect, useState, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ImportBar } from "@/components/biomarkers/ImportBar";
import { TrendsGrid } from "@/components/biomarkers/TrendsGrid";
import { MarkerTrendExplorer } from "@/components/biomarkers/MarkerTrendExplorer";
import { BiomarkerForm } from "@/components/biomarkers/BiomarkerForm";
import { BiomarkerPanels } from "@/components/biomarkers/BiomarkerPanels";
import { LabVisits } from "@/components/biomarkers/LabVisits";
import { EntriesTable } from "@/components/biomarkers/EntriesTable";
import { ExtractModal } from "@/components/biomarkers/ExtractModal";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function Biomarkers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [trends, setTrends] = useState({ markers: [], mode: "empty", latest_visit_date: null, prior_visit_date: null });
  const [marker, setMarker] = useState("Vitamin D, 25-Hydroxy");
  const [unit, setUnit] = useState("ng/mL");
  const onMarkerSelect = (label, suggestedUnit) => {
    setMarker(label);
    if (suggestedUnit) setUnit(suggestedUnit);
  };
  const [value, setValue] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractModal, setExtractModal] = useState(null); // { entries, selected, labName, labDate }
  const [visits, setVisits] = useState([]);
  const [expandedVisit, setExpandedVisit] = useState(null); // visit id
  const [visitDetail, setVisitDetail] = useState(null);
  const [credits, setCredits] = useState(null); // {monthly_quota, monthly_remaining, balance, total_available, pack}
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [showAllTrends, setShowAllTrends] = useState(false);
  const fileRef = useRef(null);
  const labRef = useRef(null);

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/biomarkers"),
      api.get("/biomarkers/trends"),
      api.get("/lab_visits"),
      api.get("/credits/balance").catch(() => ({ data: null })),
    ]);
    setItems(a.data);
    setTrends(b.data || { markers: [], mode: "empty" });
    setVisits(c.data);
    if (d.data) setCredits(d.data);
  };

  const buyCredits = async () => {
    setBuyingCredits(true);
    try {
      const { data } = await api.post("/checkout/credits", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start credit checkout.");
      setBuyingCredits(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/biomarkers", {
        marker, unit, value: parseFloat(value),
        date: date ? new Date(date).toISOString() : null,
        notes: notes || null,
      });
      setValue(""); setNotes("");
      if (data?.original_unit && data.original_unit !== data.unit) {
        toast.success(
          `Marker recorded. Converted ${data.original_value} ${data.original_unit} → ${data.value} ${data.unit}.`
        );
      } else {
        toast.success("Marker recorded.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    await api.delete(`/biomarkers/${id}`);
    load();
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/biomarkers/import_csv", {
        method: "POST",
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Import failed");
      } else {
        toast.success(`Imported ${data.inserted} entries${data.total_errors ? ` (${data.total_errors} skipped)` : ""}.`);
        if (fileRef.current) fileRef.current.value = "";
        load();
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "marker,value,unit,date,notes\nVitamin D,32,ng/mL,2026-01-15,\nHRV,42,ms,2026-01-15,morning reading\nhs-CRP,0.8,mg/L,2026-01-15,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "badi-biomarkers-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const showExtractResult = (data) => {
    if (!data.entries?.length) {
      const ctx = data.chunks > 1 || data.chunks_total > 1
        ? `Scanned ${data.pages} pages but no biomarkers were detected.`
        : "No biomarkers detected. Try a clearer scan.";
      toast.error(ctx);
      return;
    }
    const totalChunks = data.chunks || data.chunks_total || 1;
    const partial = data.failed_chunks > 0 ? ` (${data.failed_chunks}/${totalChunks} sections failed)` : "";
    toast.success(`Extracted ${data.entries.length} markers from ${data.pages} pages${partial}.`);
    if (data.credits) setCredits((c) => ({ ...(c || {}), ...data.credits }));
    setExtractModal({
      entries: data.entries,
      selected: new Set(data.entries.map((_, i) => i)),
      labName: data.lab_name || "",
      labDate: data.report_date || data.entries[0]?.date || "",
    });
  };

  const pollExtractJob = async (jobId, totalChunks) => {
    const pollToast = toast.loading(`Extracting 0/${totalChunks} sections…`);
    const startedAt = Date.now();
    const MAX_MS = 6 * 60 * 1000; // 6-minute hard ceiling
    try {
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: job } = await api.get(`/biomarkers/extract/status/${jobId}`);
        const done = job.chunks_done || 0;
        const failed = job.chunks_failed || 0;
        toast.loading(`Extracting ${done}/${totalChunks} sections${failed ? ` · ${failed} failed` : ""}…`, { id: pollToast });
        if (job.status === "done") {
          toast.dismiss(pollToast);
          showExtractResult({
            entries: job.entries,
            lab_name: job.lab_name,
            report_date: job.report_date,
            pages: job.pages,
            chunks: job.chunks_total,
            failed_chunks: job.failed_chunks,
            credits: job.credits,
          });
          return;
        }
        if (job.status === "failed") {
          toast.dismiss(pollToast);
          const msg = job.error || "Extraction failed.";
          if (/budget/i.test(msg)) {
            toast.error("AI credit exhausted. Top up your Universal Key, then try again.", { duration: 8000 });
          } else {
            toast.error(msg);
          }
          return;
        }
      }
      toast.dismiss(pollToast);
      toast.error("Extraction is taking longer than 6 minutes — it may still complete. Refresh in a bit.");
    } catch (err) {
      toast.dismiss(pollToast);
      toast.error(`Could not check job status: ${err?.response?.data?.detail || err?.message || "network error"}`);
    }
  };

  const extractLab = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    const sizeMb = (file.size / 1_000_000).toFixed(1);
    const startedToast = toast.loading(`Reading ${file.name} (${sizeMb} MB)…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/biomarkers/extract", {
        method: "POST",
        body: fd,
      });
      let data = null;
      try { data = await r.json(); } catch (parseErr) {
        console.debug("Non-JSON extract response (proxy/timeout page)", parseErr);
      }
      toast.dismiss(startedToast);

      if (!r.ok) {
        const detail = data?.detail || "";
        const budgetHit = /budget|exceeded|insufficient/i.test(typeof detail === "string" ? detail : JSON.stringify(detail));
        if (r.status === 402) {
          const msg = typeof detail === "string" ? detail : (detail?.message || "");
          if (/credit/i.test(msg)) {
            toast.error(
              (t) => (
                <span>
                  Out of credits.{" "}
                  <button
                    onClick={() => { toast.dismiss(t); buyCredits(); }}
                    className="underline font-medium text-[#C05C42]"
                    data-testid="toast-buy-credits-btn"
                  >
                    Buy 25 for $19
                  </button>
                </span>
              ),
              { duration: 10000 },
            );
          } else {
            toast.error("Lab extraction is a Vital/Sovereign feature.");
          }
        } else if (budgetHit) {
          toast.error("AI credit exhausted. Top up your Universal Key (Profile → Universal Key → Add Balance), then try again.", { duration: 8000 });
        } else if (r.status === 413) {
          toast.error("File too large for upload. Split your PDF in half and try again.");
        } else if (r.status === 504) {
          toast.error("Extraction timed out. Try uploading the PDF in smaller chunks (10-15 pages).");
        } else if (r.status === 502) {
          toast.error(typeof detail === "string" ? detail : "Extraction failed at the AI layer.");
        } else {
          toast.error(typeof detail === "string" ? detail : `Extraction failed (${r.status})`);
        }
      } else if (data?.async && data?.job_id) {
        // Background job — poll for progress
        toast(`${data.pages}-page PDF queued — extracting in ${data.chunks_total} sections.`, { icon: "📄", duration: 4000 });
        await pollExtractJob(data.job_id, data.chunks_total);
      } else {
        // Synchronous result
        showExtractResult(data);
      }
    } catch (err) {
      toast.dismiss(startedToast);
      toast.error(`Extraction failed: ${err?.message || "network error"}`);
    } finally {
      setExtracting(false);
      if (labRef.current) labRef.current.value = "";
    }
  };

  const toggleExtractRow = (idx) => {
    setExtractModal((m) => {
      if (!m) return m;
      const sel = new Set(m.selected);
      if (sel.has(idx)) sel.delete(idx); else sel.add(idx);
      return { ...m, selected: sel };
    });
  };

  const updateExtractRow = (idx, field, val) => {
    setExtractModal((m) => {
      if (!m) return m;
      const entries = m.entries.map((e, i) => (i === idx ? { ...e, [field]: val } : e));
      return { ...m, entries };
    });
  };

  const confirmBulkInsert = async () => {
    if (!extractModal) return;
    const chosen = extractModal.entries
      .filter((_, i) => extractModal.selected.has(i))
      .map((e) => ({
        ...e,
        value: typeof e.value === "string" ? parseFloat(e.value) : e.value,
      }))
      .filter((e) => !isNaN(e.value) && e.marker && e.unit);
    if (chosen.length === 0) {
      toast.error("Pick at least one row");
      return;
    }
    try {
      const r = await api.post("/biomarkers/bulk", {
        entries: chosen,
        lab_visit: {
          name: extractModal.labName || null,
          date: extractModal.labDate || null,
          source: "ocr",
        },
      });
      toast.success(`Imported ${r.data.inserted} entries from the lab.`);
      setExtractModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not insert");
    }
  };

  const toggleVisit = async (visitId) => {
    if (expandedVisit === visitId) {
      setExpandedVisit(null);
      setVisitDetail(null);
      return;
    }
    setExpandedVisit(visitId);
    setVisitDetail(null);
    try {
      const r = await api.get(`/lab_visits/${visitId}`);
      setVisitDetail(r.data);
    } catch (e) {
      toast.error("Could not load visit");
    }
  };

  const hasPaidPlan = ["vital", "sovereign"].includes(user?.plan);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-10 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-3">Your ledger</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]">Biomarkers</h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl">
          Track the instruments of your body over time. Read them alongside your protocols
          and the rhythms of the seasons.
        </p>
        <Link to="/bioage" className="mt-5 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] transition-colors" data-testid="biomarkers-bioage-link">
          See your Biological Age <ArrowRight className="w-4 h-4" />
        </Link>
        {!hasPaidPlan && (
          <div className="mt-6 inline-block bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-4 text-sm text-[#5C6359]" data-testid="biomarker-locked-note">
            Biomarker tracking is included with Vital & Sovereign memberships. You can still try it below.
          </div>
        )}

        <ImportBar
          uploading={uploading}
          extracting={extracting}
          fileRef={fileRef}
          labRef={labRef}
          handleCsvUpload={handleCsvUpload}
          extractLab={extractLab}
          downloadTemplate={downloadTemplate}
          credits={credits}
          buyCredits={buyCredits}
          buyingCredits={buyingCredits}
        />

        <TrendsGrid trends={trends} showAllTrends={showAllTrends} setShowAllTrends={setShowAllTrends} />

        <BiomarkerPanels items={items} onMarkerSelect={onMarkerSelect} />
      </section>

      <section className="px-6 sm:px-12 lg:px-20 pb-12 max-w-6xl mx-auto grid lg:grid-cols-3 gap-6">
        <MarkerTrendExplorer items={items} trends={trends} />
        <BiomarkerForm
          marker={marker}
          onMarkerSelect={onMarkerSelect}
          value={value}
          setValue={setValue}
          unit={unit}
          setUnit={setUnit}
          date={date}
          setDate={setDate}
          notes={notes}
          setNotes={setNotes}
          submit={submit}
        />
      </section>

      <LabVisits visits={visits} expandedVisit={expandedVisit} visitDetail={visitDetail} toggleVisit={toggleVisit} />

      <EntriesTable items={items} remove={remove} />

      <ExtractModal
        extractModal={extractModal}
        setExtractModal={setExtractModal}
        toggleExtractRow={toggleExtractRow}
        updateExtractRow={updateExtractRow}
        confirmBulkInsert={confirmBulkInsert}
      />

      <Footer />
    </div>
  );
}
